import { Flex, Form, Modal, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useCallback, useEffect, useState } from 'react';

import { useCalculateExternalLoyaltyV1Mutation } from '@app/api/ExternalLoyalty';
import { usePayWithBonusesMutation } from '@app/api/Loyalty';
import { CustomInputNumber } from '@app/components/CustomInputNumber';
import type { PayWithBonusesModalProps } from '@app/components/RecordModal/components/RecordPayment/types';
import { RULES_IS_REQUIRE, SAVE, SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { divideAmount } from '@app/utils';

interface PayWithBonusesFormValues {
  BonusesToExceed: number;
}

const { Text } = Typography;

export const PayWithBonusesModal: React.FC<PayWithBonusesModalProps> = ({
  isOpen,
  setIsOpen,
  availableBonuses,
  cardId,
  cardNumber,
  recordId,
  servicesTableData,
  bonusesToExceed,
  getRecordInfoHandler,
  initTable,
  fetchPayments,
}) => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const [form] = useForm();
  const { showNotification } = useNotification();
  const [maxBonusesToExceed, setMaxBonusesToExceed] = useState(0);

  const [payWithBonuses, { isLoading }] = usePayWithBonusesMutation();
  const [calculateExternalLoyaltyV1] = useCalculateExternalLoyaltyV1Mutation();

  useEffect(() => {
    if (isOpen && bonusesToExceed) {
      form.setFieldValue('BonusesToExceed', bonusesToExceed);
    }
  }, [bonusesToExceed, form, isOpen]);

  const handleCancel = () => {
    setIsOpen(false);
    form.resetFields();
  };

  const handleSubmitForm = async ({
    BonusesToExceed,
  }: PayWithBonusesFormValues) => {
    try {
      await payWithBonuses({
        recordId,
        ActiveBranchId,
        data: {
          CardId: cardId,
          CardNumber: cardNumber,
          BonusesToExceed,
          CardType: 'Bonus',
        },
      }).unwrap();

      showNotification('Скидка успешно применена', SUCCESS);
      handleCancel();

      await getRecordInfoHandler();
      await initTable();
      await fetchPayments();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const calculateExternalLoyaltyV1Handler = useCallback(async () => {
    const items = servicesTableData.map((item) => {
      return {
        product_id: String(item.key),
        quantity: item.count,
        price: item.price,
        paid: item.cost,
      };
    });

    try {
      const { max_bonuses_to_exceed } = await calculateExternalLoyaltyV1({
        card_id: cardId,
        items,
      }).unwrap();

      setMaxBonusesToExceed(max_bonuses_to_exceed);
    } catch (_) {
      showNotification(handleCatchError('Возникла ошибка при расчёте бонусов'));
    }
  }, [calculateExternalLoyaltyV1, cardId, servicesTableData, showNotification]);

  useEffect(() => {
    if (isOpen) calculateExternalLoyaltyV1Handler();
  }, [calculateExternalLoyaltyV1Handler, isOpen]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      title="Списать бонусы"
      onOk={form.submit}
      okText={SAVE}
      okButtonProps={{
        loading: isLoading,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
    >
      <Form form={form} onFinish={handleSubmitForm}>
        <Flex vertical gap={10}>
          <Flex justify="space-between" gap={16}>
            <Text>Баланс</Text>{' '}
            <Text strong>{divideAmount(availableBonuses)} бонусов</Text>
          </Flex>

          <Flex justify="space-between" gap={16}>
            <Text>Доступно к списанию</Text>
            <Text strong>{divideAmount(maxBonusesToExceed)} бонусов</Text>
          </Flex>

          <Flex vertical gap={4}>
            <Text strong className="label-require">
              Введите количество бонусов для списания
            </Text>
            <Form.Item rules={RULES_IS_REQUIRE} name={'BonusesToExceed'}>
              <CustomInputNumber
                className="required-field"
                inputValue={form.getFieldValue('BonusesToExceed')}
                isAllowed={(floatValue: number) =>
                  floatValue >= 0.01 && floatValue <= maxBonusesToExceed
                }
                onSuccess={({ value }) => {
                  form.setFieldValue('BonusesToExceed', value);
                }}
              />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  );
};
